import ContainerSimple from "../../../Components/ContainerSimple";
import InputAdmin from "../../../Components/InputFields/InputAdmin";
import Custom from "../../../Components/Buttons/Custom";
import MultipleSelectAdmin from "../../../Components/InputFields/MultipleSelectAdmin";
import BirthDatePicker from "../../../Components/DatePicker/BirthDatePicker";
import { useCallback, useRef, useState, useEffect } from "react";
import { API_BASE_URL } from "../../../config/api";

const USERS_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/users/list/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function buildUsersUrl({ page, searchTerm }) {
  const params = new URLSearchParams();

  params.set("page", String(page));

  if (searchTerm) {
    params.set("search", searchTerm);
  }

  return `${USERS_ENDPOINT}?${params.toString()}`;
}

function mergeUsers(previousUsers, nextUsers) {
  const seen = new Set(previousUsers.map((user) => user.id));
  const uniqueUsers = nextUsers.filter((user) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });

  return [...previousUsers, ...uniqueUsers];
}

function formatUserDisplay(user) {
  if (!user) return null;

  if (user.fn && user.ln) {
    return `${user.fn} ${user.ln} (${user.code})`;
  }

  if (user.email) {
    return `${user.email} (${user.code})`;
  }

  return `- (${user.code ?? "-"})`;
}

export default function CreateCouponModal({ onClose }) {
  const [selectedChallenge, setSelectedChallenge] = useState("");
  const [categories, setCategories] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [users, setUsers] = useState([]);

  const [displayData, setDisplayData] = useState(null);
  const [displayUser, setDisplayUser] = useState(null);

  const [name, setName] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [limit, setLimit] = useState(null);
  const [expiredAt, setExpiredAt] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedUserTarget, setSelectedUserTarget] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchState, setUserSearchState] = useState("");
  const [appliedUserSearch, setAppliedUserSearch] = useState("");
  const [userPagination, setUserPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isUserLoadingMore, setIsUserLoadingMore] = useState(false);

  const userRequestIdRef = useRef(0);

  useEffect(() => console.log(selectedChallenge));

  useEffect(() => {
    if (!selectedUser) {
      setDisplayUser(null);
      return;
    }

    const user = users.find((item) => item.id === selectedUser);

    if (user) {
      setDisplayUser(formatUserDisplay(user));
    }
  }, [selectedUser, users]);

  const fetchUserData = useCallback(
    async ({ page = 1, replace = false, searchTerm }) => {
      const requestId = userRequestIdRef.current + 1;
      userRequestIdRef.current = requestId;

      if (replace) {
        setIsUserLoading(true);
        setIsUserLoadingMore(false);
      } else {
        setIsUserLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildUsersUrl({
            page,
            searchTerm,
          }),
        );

        if (!data || requestId !== userRequestIdRef.current) {
          return;
        }

        const nextUsers = Array.isArray(data.results) ? data.results : [];

        setUsers((previousUsers) =>
          replace ? nextUsers : mergeUsers(previousUsers, nextUsers),
        );
        setUserPagination({
          page: data.page ?? page,
          count: data.count ?? nextUsers.length,
          pageSize: data.page_size ?? nextUsers.length,
          totalPages: data.total_pages ?? page,
        });
      } catch (error) {
        if (requestId !== userRequestIdRef.current) {
          return;
        }

        console.error("Error fetching user data:", error);

        if (replace) {
          setUsers([]);
        }
      } finally {
        if (requestId === userRequestIdRef.current) {
          setIsUserLoading(false);
          setIsUserLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedUserTarget !== "برای کاربر") {
      return;
    }

    fetchUserData({
      page: 1,
      replace: true,
      searchTerm: appliedUserSearch,
    });
  }, [appliedUserSearch, fetchUserData, selectedUserTarget]);

  const handleCreate = async () => {
    if (
      !name ||
      !discount ||
      !limit ||
      !expiredAt ||
      !selectedChallenge ||
      (selectedUserTarget === "برای کاربر" && !selectedUser)
    ) {
      return;
    }
    const selectedUserData = users.find((user) => user.id === selectedUser);
    const jsonData = {
      name: name,
      discount_percent: discount,
      limits: limit,
      expired_at: expiredAt,
    };
    if (selectedChallenge === "انتخاب یک چالش") {
      jsonData["challenge"] = selectedTarget;
    } else if (selectedChallenge === "انتخاب یک دسته") {
      jsonData["challenge_category"] = selectedTarget;
    } else if (selectedChallenge === "انتخاب همه") {
      jsonData["is_for_all_challenges"] = true;
    }
    if (selectedUserTarget === "برای کاربر") {
      jsonData["user_code"] = selectedUserData?.code;
    }
    try {
      let response = await fetch(
        `${API_BASE_URL}/api/v0/challenges/admin/coupons/create/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify(jsonData),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          window.location.href = "/signin";
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}/api/v0/challenges/admin/coupons/create/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify(jsonData),
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      onClose();
      window.location.reload();
    } catch (error) {
      console.error("Error fetching categories data:", error);
    }
  };

  useEffect(() => {
    console.log("Selected Target:", selectedTarget);
  });
  const onRowClick = (rowData) => {
    if (!rowData) {
      setDisplayData(null);
      setSelectedTarget(null);
      return;
    }
    if (selectedChallenge === "انتخاب یک چالش") {
      setDisplayData(
        challenges.find((challenge) => challenge.id === rowData).name,
      );
    } else if (selectedChallenge === "انتخاب یک دسته") {
      setDisplayData(rowData);
    }
    setSelectedTarget(rowData);
  };
  const handleUserSearchClick = () => {
    const nextSearch = userSearchState.trim();

    if (nextSearch === appliedUserSearch) {
      fetchUserData({
        page: 1,
        replace: true,
        searchTerm: nextSearch,
      });
      return;
    }

    setAppliedUserSearch(nextSearch);
  };
  const handleUserTableScroll = (event) => {
    const element = event.currentTarget;

    if (
      isUserLoading ||
      isUserLoadingMore ||
      userPagination.page >= userPagination.totalPages
    ) {
      return;
    }

    if (element.scrollTop + element.clientHeight < element.scrollHeight - 80) {
      return;
    }

    fetchUserData({
      page: userPagination.page + 1,
      replace: false,
      searchTerm: appliedUserSearch,
    });
  };
  const onUserClick = (userId) => {
    setSelectedUser(userId);

    if (!userId) {
      setDisplayUser(null);
    }
  };
  const fetchChallengesData = async () => {
    try {
      let response = await fetch(
        `${API_BASE_URL}/api/v0/challenges/admin/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          window.location.href = "/signin";
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}/api/v0/challenges/admin/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Categories Data:", data);
      setChallenges(data);
    } catch (error) {
      console.error("Error fetching categories data:", error);
    }
  };
  const fetchCategoriesData = async () => {
    try {
      let response = await fetch(
        `${API_BASE_URL}/api/v0/challenges/admin/categories/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          window.location.href = "/signin";
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}/api/v0/challenges/admin/categories/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Categories Data:", data);
      setCategories(data.categories);
    } catch (error) {
      console.error("Error fetching categories data:", error);
    }
  };
  useEffect(() => {
    fetchCategoriesData();
    fetchChallengesData();
  }, []);
  return (
    <ContainerSimple>
      <div className="w-full flex gap-12">
        <InputAdmin
          label="کد تخفیف"
          id="type"
          name="type"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <InputAdmin
          label="درصد تخفیف"
          id="type"
          name="type"
          type="number"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
        />
      </div>
      <div className="w-full flex gap-12">
        {/* <InputAdmin
          label="مهلت روزهای استفاده پس از خرید"
          id="type"
          name="type"
          type="number"
        /> */}
        <InputAdmin
          label="محدودیت تعداد استفاده"
          id="type"
          name="type"
          type="number"
          value={limit}
          // isLtr={true}
          onChange={(e) => setLimit(e.target.value)}
        />
        <BirthDatePicker
          label="تاریخ انقضا"
          value={expiredAt || ""}
          onChange={setExpiredAt}
          isReadOnly={false}
          variant="admin"
          minYearsAgo={10}
          maxYearsFromNow={20}
        />
      </div>
      <div className="w-full flex gap-12">
        <InputAdmin
          label="مناسب برای چالش"
          id="type"
          name="type"
          type="select"
          selectOptions={["انتخاب یک چالش", "انتخاب یک دسته", "انتخاب همه"]}
          selectPlaceHolder="انتخاب کنید"
          onChange={(e) => {
            setSelectedChallenge(e.target.value);
            setSelectedTarget(null);
            setDisplayData(null);
          }}
        />
        <div className="w-full"></div>
      </div>
      <div className="w-full flex gap-12">
        <InputAdmin
          label="مناسب برای کاربر"
          id="user-target"
          name="user-target"
          type="select"
          selectOptions={["برای همه", "برای کاربر"]}
          selectPlaceHolder="انتخاب کنید"
          value={selectedUserTarget || ""}
          onChange={(e) => {
            setSelectedUserTarget(e.target.value);
            setSelectedUser(null);
            setDisplayUser(null);
          }}
        />
        <div className="w-full"></div>
      </div>
      {selectedUserTarget === "برای کاربر" && (
        <MultipleSelectAdmin
          header="انتخاب کاربر"
          search={true}
          searchValue={userSearchState}
          setSearchState={setUserSearchState}
          searchClick={handleUserSearchClick}
          footer="کاربر انتخاب شده"
          footerValue={displayUser}
          onRowClick={onUserClick}
          onTableScroll={handleUserTableScroll}
          isLoadingMore={isUserLoading || isUserLoadingMore}
          tableColumns={[
            { key: "code", label: "کد" },
            { key: "name", label: "نام" },
          ]}
          tableData={users.map((user) => ({
            id: user.id,
            code: user.code,
            name:
              user.fn && user.ln ? `${user.fn} ${user.ln}` : (user.email ?? "-"),
          }))}
        />
      )}
      {selectedChallenge === "انتخاب یک چالش" && (
        <MultipleSelectAdmin
          header="انتخاب یک چالش"
          search={false}
          footer="چالش انتخاب شده"
          footerValue={displayData}
          tableColumns={[
            { key: "code", label: "کد چالش" },
            { key: "category", label: "دسته چالش" },
            { key: "name", label: "نام چالش" },
          ]}
          tableData={challenges?.map((challenge) => ({
            id: challenge.id,
            code: challenge.id,
            category: challenge.category,
            name: challenge.name,
          }))}
          onRowClick={onRowClick}
        />
      )}
      {selectedChallenge === "انتخاب یک دسته" && (
        <MultipleSelectAdmin
          header="انتخاب یک دسته"
          search={false}
          footer="دسته انتخاب شده"
          footerValue={displayData}
          tableColumns={[{ key: "category", label: "دسته چالش" }]}
          tableData={categories?.map((category) => ({
            id: category,
            category: category,
          }))}
          onRowClick={onRowClick}
        />
      )}
      <div className="w-full flex gap-2.5 mt-6">
        <Custom
          text="انصراف و بستن"
          svg_name="xMark"
          svgClassName="stroke-zi-text-light dark:stroke-zi-text-dark"
          href=""
          className="!rounded-lg !p-2.5 w-full text-zi-text-light dark:text-zi-text-dark ring-1 ring-zi-border-dark bg-transparent"
          onClick={onClose}
        />
        <Custom
          text="ایجاد کد تخفیف جدید"
          svg_name="checkMark"
          svgClassName="stroke-zi-text-dark"
          href=""
          className="!rounded-lg !p-2.5 w-full text-zi-text-dark bg-zi-gradient-button-blue"
          textShadow={true}
          svgShadow={true}
          onClick={handleCreate}
        />
      </div>
    </ContainerSimple>
  );
}
